/**
 * @module components/CaseZeiterfassungPanel
 * @description Zeiterfassung-Integration in Cases-Detail (#989, v2.426.0).
 *
 * Zeigt: Aktiver Timer (falls auf diesem Case), Start/Stop-Button, Liste der
 * bisherigen Einträge zu diesem Case mit Summe.
 * Schreibt nur eigene Einträge (Backend gated). Strikt additiv — Cases werden nicht geändert.
 *
 * @since v2.426.0
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, Play, Pause, Square, X, Plus } from 'lucide-react';

const API = '';

function pad2(n) { return n < 10 ? `0${n}` : `${n}`; }
function formatTimerSec(sec) {
  if (sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}
// Netto-Elapsed in Sekunden (#994): brutto - accumulated_pause - laufender Pause-Block.
function computeElapsedSec(timer, nowMs) {
  if (!timer || !timer.start_at) return 0;
  const startMs = new Date(timer.start_at).getTime();
  const accPauseMs = (parseInt(timer.accumulated_pause_min, 10) || 0) * 60000;
  const refMs = timer.paused_at ? new Date(timer.paused_at).getTime() : nowMs;
  return Math.max(0, Math.floor((refMs - startMs - accPauseMs) / 1000));
}
function formatHours(min) {
  if (!min && min !== 0) return '—';
  return (min / 60).toFixed(2).replace('.', ',') + ' h';
}
function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}. ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export default function CaseZeiterfassungPanel({ caseId, kundeId, t, compact = false }) {
  const [active, setActive] = useState(null);
  const [entries, setEntries] = useState([]);
  const [kategorien, setKategorien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStart, setShowStart] = useState(false);
  const [showStop, setShowStop] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  // Tick
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, eRes, kRes] = await Promise.all([
        fetch(`${API}/api/zeiterfassung/timer/offene`, { credentials: 'include' }),
        fetch(`${API}/api/zeiterfassung/eintraege?case_id=${caseId}&limit=200`, { credentials: 'include' }),
        fetch(`${API}/api/zeiterfassung/kategorien`, { credentials: 'include' }),
      ]);
      if (eRes.status === 403) { setForbidden(true); setLoading(false); return; }
      const tData = await tRes.json();
      const eData = await eRes.json();
      const kData = await kRes.json();
      // #996 Multi-Timer: aus offene[] den Timer für DIESEN Case finden (running ODER paused)
      const offene = Array.isArray(tData.offene) ? tData.offene : [];
      const myTimer = offene.find(o => o.case_id === caseId) || null;
      setActive(myTimer);
      setEntries(eData.eintraege || []);
      setKategorien(kData.kategorien || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { if (caseId) load(); }, [caseId, load]);

  if (forbidden) return null;

  async function saveManualEntry({ datum, vonZeit, bisZeit, dauerMin, eingabeArt, kategorie_id, beschreibung, billable }) {
    setError(null);
    let startAt = null, endAt = null, dauer = null;
    if (eingabeArt === 'zeit') {
      if (!vonZeit || !bisZeit) { setError('Von und Bis erforderlich'); return; }
      startAt = new Date(`${datum}T${vonZeit}:00`).toISOString();
      endAt = new Date(`${datum}T${bisZeit}:00`).toISOString();
      if (new Date(endAt) <= new Date(startAt)) { setError('Bis muss nach Von liegen'); return; }
    } else {
      const min = parseInt(dauerMin, 10);
      if (!min || min < 1) { setError('Dauer in Minuten erforderlich'); return; }
      const start = new Date(`${datum}T08:00:00`);
      const end = new Date(start.getTime() + min * 60000);
      startAt = start.toISOString();
      endAt = end.toISOString();
      dauer = min;
    }
    const body = {
      kategorie_id,
      kunde_id: kundeId || null,
      case_id: caseId,
      start_at: startAt,
      end_at: endAt,
      beschreibung,
      billable,
    };
    if (dauer != null) body.dauer_min = dauer;
    const res = await fetch(`${API}/api/zeiterfassung/eintraege`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || 'Speichern fehlgeschlagen');
      return;
    }
    setShowManual(false);
    await load();
  }

  async function startTimer({ kategorie_id, beschreibung, billable }) {
    setError(null);
    const res = await fetch(`${API}/api/zeiterfassung/timer/start`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kategorie_id, kunde_id: kundeId || null, case_id: caseId, beschreibung, billable }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || 'Start fehlgeschlagen');
      return;
    }
    setShowStart(false);
    await load();
  }

  async function stopTimer({ beschreibung, kategorie_id, billable }) {
    setError(null);
    const res = await fetch(`${API}/api/zeiterfassung/timer/stop`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: active?.id, beschreibung, kategorie_id, billable }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || 'Stop fehlgeschlagen');
      return;
    }
    setShowStop(false);
    await load();
  }

  async function togglePause() {
    if (!active) return;
    setError(null);
    const path = active.paused_at ? '/api/zeiterfassung/timer/resume' : '/api/zeiterfassung/timer/pause';
    const res = await fetch(`${API}${path}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: active.id }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(d.error || (active.paused_at ? 'Resume fehlgeschlagen' : 'Pause fehlgeschlagen'));
      return;
    }
    await load();
  }

  const stopped = entries.filter(e => e.end_at);
  const summe = stopped.reduce((acc, e) => acc + (e.dauer_min || 0), 0);
  const billableSum = stopped.filter(e => e.billable).reduce((acc, e) => acc + (e.dauer_min || 0), 0);
  const elapsed = computeElapsedSec(active, now);
  const isPaused = !!(active && active.paused_at);

  const modals = (
    <>
      {showStart && (
        <StartModal kategorien={kategorien} onClose={() => setShowStart(false)} onSubmit={startTimer} t={t} />
      )}
      {showStop && active && (
        <StopModal timer={active} kategorien={kategorien} elapsed={elapsed} onClose={() => setShowStop(false)} onSubmit={stopTimer} t={t} />
      )}
      {showManual && (
        <ManualEntryModal kategorien={kategorien} onClose={() => setShowManual(false)} onSubmit={saveManualEntry} t={t} />
      )}
    </>
  );

  // #1075 Compact-Variante für rechte Sidebar (Cases Detail-View). Funktion 100% identisch,
  // nur Layout verdichtet — passt zwischen Anhänge und Erstellt-Zeitstempel in Card 2.
  if (compact) {
    return (
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-semibold uppercase tracking-[0.8px]" style={{ color: 'var(--text-tertiary)' }}>
            {t('zeiterfassung.case.title', 'Zeiterfassung')}
            {stopped.length > 0 && <span className="ml-1 normal-case font-medium tracking-normal">({formatHours(summe)})</span>}
          </label>
          {billableSum > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-100 text-emerald-700 tabular-nums">
              {formatHours(billableSum)} €
            </span>
          )}
        </div>

        {active ? (
          <div className={`flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-md mb-1.5 ${isPaused ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold tabular-nums ${isPaused ? 'text-amber-800 dark:text-amber-200' : 'text-blue-800 dark:text-blue-200'}`}>
              {isPaused ? <Pause className="w-3 h-3" /> : (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
              )}
              {formatTimerSec(elapsed)}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={togglePause}
                title={isPaused ? t('zeiterfassung.case.resume', 'Fortsetzen') : t('zeiterfassung.case.pause', 'Pause')}
                className={`w-6 h-6 rounded inline-flex items-center justify-center ${isPaused ? 'bg-[#338edc] text-white hover:bg-[#1f6fb5]' : 'bg-amber-500 text-white hover:bg-amber-600'}`}>
                {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
              </button>
              <button
                onClick={() => setShowStop(true)}
                title={t('zeiterfassung.case.stop', 'Stop')}
                className="w-6 h-6 rounded inline-flex items-center justify-center bg-[#EF4444] text-white hover:bg-[#DC2626]">
                <Square className="w-3 h-3" />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-1.5 mb-1.5">
            <button onClick={() => setShowManual(true)} className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md border border-[var(--border-card,#E5E7EB)] bg-white text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-inset,#F8F9FC)] text-[11px] font-medium">
              <Plus className="w-3 h-3" />
              {t('zeiterfassung.case.manualShort', 'Manuell')}
            </button>
            <button onClick={() => setShowStart(true)} className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-[#338edc] text-white hover:bg-[#1f6fb5] text-[11px] font-medium">
              <Play className="w-3 h-3" />
              {t('zeiterfassung.case.startShort', 'Start')}
            </button>
          </div>
        )}

        {error && <div className="text-[10px] text-red-600 mb-1.5">{error}</div>}

        {loading ? (
          <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] italic">{t('common.loading', 'Lädt …')}</div>
        ) : stopped.length === 0 && !active ? (
          <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] italic">{t('zeiterfassung.case.empty', 'Noch keine Zeit erfasst.')}</div>
        ) : stopped.length > 0 ? (
          <div className="space-y-0.5">
            {stopped.slice(0, 8).map(e => (
              <div key={e.id} className="flex items-center gap-2 text-[11px] px-1.5 py-1 rounded-md hover:bg-[var(--bg-inset,#F8F9FC)] dark:hover:bg-[var(--bg-card-hover)] transition-colors">
                <span className="text-[var(--text-tertiary,#6B7280)] tabular-nums flex-shrink-0 w-[68px]">{fmtDate(e.start_at)}</span>
                <span className="flex-1 truncate text-[var(--text-primary,#1A1A2E)]" title={`${e.kategorie_name || ''}${e.beschreibung ? ' — ' + e.beschreibung : ''}`}>
                  {e.kategorie_name || '—'}
                </span>
                <span className="font-medium text-[var(--text-primary,#1A1A2E)] tabular-nums flex-shrink-0">{formatHours(e.dauer_min)}</span>
                {e.billable && <span className="px-1 py-0 rounded bg-emerald-100 text-emerald-700 text-[9px] font-medium flex-shrink-0">€</span>}
              </div>
            ))}
            {stopped.length > 8 && (
              <div className="text-[10px] text-[var(--text-tertiary,#6B7280)] text-center pt-1">
                +{stopped.length - 8} {t('zeiterfassung.case.moreEntries', 'weitere')}
              </div>
            )}
          </div>
        ) : null}

        {modals}
      </div>
    );
  }

  // Default-Variante (Body-Mount, Legacy — falls anderswo verwendet)
  return (
    <div className="mx-8 mt-4 border border-[var(--border-default,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--bg-inset,#F8F9FC)] border-b border-[var(--border-default,#E5E7EB)]">
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)]" />
          <span className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">{t('zeiterfassung.case.title', 'Zeiterfassung')}</span>
          {stopped.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 tabular-nums">
              {formatHours(summe)}
            </span>
          )}
          {billableSum > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700 tabular-nums">
              {formatHours(billableSum)} {t('zeiterfassung.case.billable', 'abrechenbar')}
            </span>
          )}
        </div>
        {active ? (
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-[8px] text-xs font-semibold tabular-nums ${isPaused ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'}`}>
              {isPaused ? <Pause className="w-3 h-3" /> : (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
              )}
              {formatTimerSec(elapsed)}
            </span>
            <button onClick={togglePause} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] text-xs font-medium ${isPaused ? 'bg-[#338edc] text-white hover:bg-[#1f6fb5]' : 'bg-amber-500 text-white hover:bg-amber-600'}`}>
              {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
              {isPaused ? t('zeiterfassung.case.resume', 'Fortsetzen') : t('zeiterfassung.case.pause', 'Pause')}
            </button>
            <button onClick={() => setShowStop(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] bg-[#EF4444] text-white hover:bg-[#DC2626] text-xs font-medium">
              <Square className="w-3 h-3" />
              {t('zeiterfassung.case.stop', 'Stop')}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <button onClick={() => setShowManual(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[var(--text-primary,#1A1A2E)] hover:bg-[var(--bg-inset,#F8F9FC)] text-xs font-medium">
              <Plus className="w-3 h-3" />
              {t('zeiterfassung.case.manual', 'Manueller Eintrag')}
            </button>
            <button onClick={() => setShowStart(true)} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[8px] bg-[#338edc] text-white hover:bg-[#1f6fb5] text-xs font-medium">
              <Play className="w-3 h-3" />
              {t('zeiterfassung.case.start', 'Timer starten')}
            </button>
          </div>
        )}
      </div>

      {error && <div className="px-4 py-2 text-xs text-red-600 bg-red-50 border-b border-red-200">{error}</div>}

      {loading ? (
        <div className="px-4 py-3 text-xs text-[var(--text-tertiary,#6B7280)]">{t('common.loading', 'Lädt …')}</div>
      ) : stopped.length === 0 && !active ? (
        <div className="px-4 py-3 text-xs text-[var(--text-tertiary,#6B7280)]">{t('zeiterfassung.case.empty', 'Noch keine Zeit erfasst.')}</div>
      ) : (
        <div className="divide-y divide-[var(--border-table,#F3F4F6)]">
          {stopped.map(e => (
            <div key={e.id} className="flex items-center gap-3 px-4 py-2 text-xs">
              <span className="w-24 text-[var(--text-tertiary,#6B7280)] tabular-nums flex-shrink-0">{fmtDate(e.start_at)}</span>
              <span className="flex-1 truncate text-[var(--text-primary,#1A1A2E)]" title={e.beschreibung}>{e.kategorie_name || '—'}{e.beschreibung ? ` · ${e.beschreibung}` : ''}</span>
              <span className="text-[var(--text-secondary,#4B5563)] tabular-nums flex-shrink-0">{(e.ma_vorname || '') + ' ' + (e.ma_nachname || '')}</span>
              <span className="font-medium text-[var(--text-primary,#1A1A2E)] tabular-nums flex-shrink-0">{formatHours(e.dauer_min)}</span>
              {e.billable && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-medium flex-shrink-0">€</span>}
            </div>
          ))}
        </div>
      )}

      {modals}
    </div>
  );
}

function ModalShell({ title, onClose, children, footer }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-[14px] border border-[var(--border-card,#E5E7EB)] w-full max-w-[460px] mt-12 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-card,#E5E7EB)]">
          <div className="text-[15px] font-semibold text-[var(--text-primary)]">{title}</div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] rounded-b-[14px]">{footer}</div>}
      </div>
    </div>
  );
}

function StartModal({ kategorien, onClose, onSubmit, t }) {
  const [kategorieId, setKategorieId] = useState(kategorien[0]?.id ? String(kategorien[0].id) : '');
  const [beschreibung, setBeschreibung] = useState('');
  // #998 Quickfix: "Abrechenbar" NIE per Default aktiv — User entscheidet bewusst.
  const [billable, setBillable] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <ModalShell
      title={t('zeiterfassung.case.startTitle', 'Timer starten')}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.cancel', 'Abbrechen')}</button>
          <button onClick={async () => { setBusy(true); await onSubmit({ kategorie_id: parseInt(kategorieId, 10), beschreibung, billable }); setBusy(false); }} disabled={!kategorieId || busy} className="px-4 py-1.5 rounded-[8px] bg-[#338edc] text-white hover:bg-[#1f6fb5] text-[13px] font-medium disabled:opacity-50">{t('zeiterfassung.case.start', 'Starten')}</button>
        </>
      }
    >
      <div>
        <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.case.kategorie', 'Kategorie')}</div>
        <select value={kategorieId} onChange={e => setKategorieId(e.target.value)}
          className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]">
          {kategorien.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </div>
      <div>
        <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.case.beschreibung', 'Beschreibung')}</div>
        <input type="text" value={beschreibung} onChange={e => setBeschreibung(e.target.value)}
          className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]"
          placeholder={t('zeiterfassung.case.beschreibungPlaceholder', 'Optional')} />
      </div>
      <label className="flex items-center gap-2 text-[13px] cursor-pointer">
        <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} />
        {t('zeiterfassung.case.billableLabel', 'Abrechenbar (Kunde verrechnen)')}
      </label>
    </ModalShell>
  );
}

function StopModal({ timer, kategorien, elapsed, onClose, onSubmit, t }) {
  const [beschreibung, setBeschreibung] = useState(timer?.beschreibung || '');
  const [kategorieId, setKategorieId] = useState(timer?.kategorie_id ? String(timer.kategorie_id) : '');
  // #998 Quickfix: "Abrechenbar" NIE per Default aktiv — Stop erzeugt einen neuen Eintrag, User entscheidet bewusst.
  const [billable, setBillable] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <ModalShell
      title={t('zeiterfassung.case.stopTitle', 'Timer stoppen')}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.cancel', 'Abbrechen')}</button>
          <button onClick={async () => { setBusy(true); await onSubmit({ beschreibung, kategorie_id: kategorieId ? parseInt(kategorieId, 10) : undefined, billable }); setBusy(false); }} disabled={busy} className="px-4 py-1.5 rounded-[8px] bg-[#EF4444] text-white hover:bg-[#DC2626] text-[13px] font-medium disabled:opacity-50">{t('zeiterfassung.case.stop', 'Stoppen')}</button>
        </>
      }
    >
      <div className="rounded-[10px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] p-3 flex items-center justify-between">
        <div className="text-[12px] text-[var(--text-tertiary)]">{t('zeiterfassung.case.dauer', 'Dauer')}</div>
        <div className="text-[18px] font-bold text-[var(--text-primary)] tabular-nums">{formatTimerSec(elapsed)}</div>
      </div>
      <div>
        <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.case.kategorie', 'Kategorie')}</div>
        <select value={kategorieId} onChange={e => setKategorieId(e.target.value)}
          className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]">
          {kategorien.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </div>
      <div>
        <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.case.beschreibung', 'Beschreibung')}</div>
        <textarea value={beschreibung} onChange={e => setBeschreibung(e.target.value)}
          rows={3} className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]" />
      </div>
      <label className="flex items-center gap-2 text-[13px] cursor-pointer">
        <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} />
        {t('zeiterfassung.case.billableLabel', 'Abrechenbar (Kunde verrechnen)')}
      </label>
    </ModalShell>
  );
}

// #995 Punkt 0 — Manueller Eintrag direkt aus Case-Detail (case_id wird automatisch gesetzt)
function ManualEntryModal({ kategorien, onClose, onSubmit, t }) {
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${pad2(today.getMonth() + 1)}-${pad2(today.getDate())}`;
  const [datum, setDatum] = useState(todayIso);
  const [eingabeArt, setEingabeArt] = useState('dauer');
  const [vonZeit, setVonZeit] = useState('');
  const [bisZeit, setBisZeit] = useState('');
  const [dauerMin, setDauerMin] = useState('');
  const [kategorieId, setKategorieId] = useState(kategorien[0]?.id ? String(kategorien[0].id) : '');
  const [beschreibung, setBeschreibung] = useState('');
  // #998 Quickfix: "Abrechenbar" NIE per Default aktiv — User entscheidet bewusst.
  const [billable, setBillable] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <ModalShell
      title={t('zeiterfassung.case.manualTitle', 'Manueller Eintrag')}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">{t('common.cancel', 'Abbrechen')}</button>
          <button
            onClick={async () => {
              setBusy(true);
              await onSubmit({
                datum, vonZeit, bisZeit, dauerMin, eingabeArt,
                kategorie_id: parseInt(kategorieId, 10),
                beschreibung, billable,
              });
              setBusy(false);
            }}
            disabled={!kategorieId || busy}
            className="px-4 py-1.5 rounded-[8px] bg-[#338edc] text-white hover:bg-[#1f6fb5] text-[13px] font-medium disabled:opacity-50"
          >{t('common.save', 'Speichern')}</button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.datum', 'Datum')}</div>
          <input type="date" value={datum} onChange={e => setDatum(e.target.value)}
            className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]" />
        </div>
        <div>
          <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.eingabeArt', 'Eingabe')}</div>
          <select value={eingabeArt} onChange={e => setEingabeArt(e.target.value)}
            className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]">
            <option value="dauer">{t('zeiterfassung.eingabeDauer', 'Nur Dauer (Minuten)')}</option>
            <option value="zeit">{t('zeiterfassung.eingabeZeit', 'Von – Bis Uhrzeit')}</option>
          </select>
        </div>
      </div>

      {eingabeArt === 'zeit' ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.von', 'Von')}</div>
            <input type="time" value={vonZeit} onChange={e => setVonZeit(e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]" />
          </div>
          <div>
            <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.bis', 'Bis')}</div>
            <input type="time" value={bisZeit} onChange={e => setBisZeit(e.target.value)}
              className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]" />
          </div>
        </div>
      ) : (
        <div>
          <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.dauerMin', 'Dauer (Minuten)')}</div>
          <input type="number" min="1" value={dauerMin} onChange={e => setDauerMin(e.target.value)}
            className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]" />
        </div>
      )}

      <div>
        <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.case.kategorie', 'Kategorie')}</div>
        <select value={kategorieId} onChange={e => setKategorieId(e.target.value)}
          className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]">
          {kategorien.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      </div>
      <div>
        <div className="text-[12px] text-[var(--text-tertiary)] mb-1 font-medium">{t('zeiterfassung.case.beschreibung', 'Beschreibung')}</div>
        <textarea value={beschreibung} onChange={e => setBeschreibung(e.target.value)}
          rows={3} className="w-full px-2.5 py-1.5 rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-white text-[13px]" />
      </div>
      <label className="flex items-center gap-2 text-[13px] cursor-pointer">
        <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} />
        {t('zeiterfassung.case.billableLabel', 'Abrechenbar (Kunde verrechnen)')}
      </label>
    </ModalShell>
  );
}
